import { Document_Competency_Map_Question_Type, Grade } from "@generated/graphql";
import { EVALUATION_TYPE_OPTIONS, LANGUAGE_BUTTONS, MAX_ESTIMATED_DURATION, QUESTION_TYPE_OPTIONS } from "./constants";
import {
  CompetencyMapGenerationFormValues,
  EvaluationType,
  GenerationQuestionMetrics,
  QuestionTypeCountMap,
  QuestionTypeMixItem,
} from "./types";

export const getQuestionTypeOption = (questionType: Document_Competency_Map_Question_Type) =>
  QUESTION_TYPE_OPTIONS.find((item) => item.id === questionType);

export const getQuestionTypeCount = (
  questionTypeCounts: QuestionTypeCountMap,
  questionType: Document_Competency_Map_Question_Type
) => questionTypeCounts[questionType] || 0;

export const getDifficultyGradeLabel = (difficulty: number) => {
  if (difficulty <= 3) return Grade.Junior;
  if (difficulty <= 6) return Grade.Middle;
  return Grade.Senior;
};

export const getDifficultyCoefficient = (difficulty: number) => 0.7 + (difficulty / 10) * 0.6;

export const getEvaluationTypeByDuration = (durationMinutes: number): EvaluationType => {
  if (durationMinutes <= 30) return "screening";
  if (durationMinutes <= 90) return "assessment";
  return "extended";
};

export const getEvaluationTypeLabel = (evaluationType: EvaluationType) =>
  EVALUATION_TYPE_OPTIONS.find((item) => item.id === evaluationType)?.label || EVALUATION_TYPE_OPTIONS[0].label;

export const getEvaluationTypeTokens = (evaluationType: EvaluationType) =>
  EVALUATION_TYPE_OPTIONS.find((item) => item.id === evaluationType)?.tokens || EVALUATION_TYPE_OPTIONS[0].tokens;

export const getQuestionTypeConstraints = (questionTypeCounts: QuestionTypeCountMap) =>
  QUESTION_TYPE_OPTIONS.reduce<Array<{ amount: number; questionType: Document_Competency_Map_Question_Type }>>(
    (acc, item) => {
      const amount = getQuestionTypeCount(questionTypeCounts, item.id);

      if (!amount) {
        return acc;
      }

      return [...acc, { amount, questionType: item.id }];
    },
    []
  );

export const calculateEstimatedDuration = (questionTypeCounts: QuestionTypeCountMap, difficulty: number) => {
  const difficultyCoefficient = getDifficultyCoefficient(difficulty);

  return Math.round(
    QUESTION_TYPE_OPTIONS.reduce((total, item) => {
      const count = getQuestionTypeCount(questionTypeCounts, item.id);

      if (!count) {
        return total;
      }

      return total + count * item.durationMinutes * difficultyCoefficient;
    }, 0)
  );
};

export const getTimeIndicatorPercent = (durationMinutes: number) =>
  Math.min(100, Math.round((durationMinutes / MAX_ESTIMATED_DURATION) * 100));

export const getQuestionTypeMix = (questionTypeCounts: QuestionTypeCountMap): QuestionTypeMixItem[] => {
  const totalQuestions = QUESTION_TYPE_OPTIONS.reduce(
    (total, item) => total + getQuestionTypeCount(questionTypeCounts, item.id),
    0
  );

  if (!totalQuestions) {
    return [];
  }

  return QUESTION_TYPE_OPTIONS.reduce<QuestionTypeMixItem[]>((acc, item) => {
    const count = getQuestionTypeCount(questionTypeCounts, item.id);

    if (!count) {
      return acc;
    }

    return [
      ...acc,
      {
        id: item.id,
        label: item.shortLabel,
        count,
        percent: Math.round((count / totalQuestions) * 100),
      },
    ];
  }, []);
};

export const calculateQuestionMetrics = (
  questionTypeCounts: QuestionTypeCountMap,
  difficulty: number
): GenerationQuestionMetrics => {
  const estimatedDuration = calculateEstimatedDuration(questionTypeCounts, difficulty);
  const evaluationType = getEvaluationTypeByDuration(estimatedDuration);
  const mix = getQuestionTypeMix(questionTypeCounts);
  const totalQuestions = mix.reduce((total, item) => total + item.count, 0);

  return {
    estimatedDuration,
    totalQuestions,
    totalActiveTypes: mix.length,
    evaluationType,
    tokens: getEvaluationTypeTokens(evaluationType),
    indicatorPercent: getTimeIndicatorPercent(estimatedDuration),
    mix,
  };
};

export const getDefaultQuestionTypeCounts = (): QuestionTypeCountMap =>
  QUESTION_TYPE_OPTIONS.reduce(
    (acc, item) => ({
      ...acc,
      [item.id]: item.defaultCount,
    }),
    {} as QuestionTypeCountMap
  );

export const getDefaultGenerationFormValues = (): CompetencyMapGenerationFormValues => {
  const questionTypeCounts = getDefaultQuestionTypeCounts();

  return {
    title: "",
    specialization: "",
    files: [],
    difficulty: 5,
    durationMinutes: calculateEstimatedDuration(questionTypeCounts, 5),
    language: LANGUAGE_BUTTONS[0].id,
    questionTypeCounts,
    globalPrompt: "",
    questionsPrompt: "",
    criteriaPrompt: "",
    tonePrompt: "",
    toneContext: "",
  };
};

export const buildTonePrompt = (tonePrompt: string, toneContext: string) =>
  [tonePrompt.trim(), toneContext.trim()].filter(Boolean).join("\n");

export const formatFileSize = (size: number) => {
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.round(size / 1024)} KB`;
  return `${size} B`;
};
